"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { signIn } from "next-auth/react";
import { useState } from "react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";

const SignInPage = () => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const searchParams = useSearchParams();
    const callbackUrl = (() => {
        const p = searchParams.get("callbackUrl");
        if (!p) return "/v1";
        try {
            return decodeURIComponent(p);
        } catch {
            return p;
        }
    })();

    const handleGoogleSignIn = async () => {
        setIsLoading(true);
        setError(null);
        try {
            await signIn("google", { callbackUrl });
        } catch (error) {
            setError("Error al iniciar sesión con Google");
            setIsLoading(false);
        }
    };

    const handleEmailSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setIsLoading(true);
        setError(null);

        try {
            await signIn("resend", {
                email,
                callbackUrl,
                redirect: true
            });
        } catch (error) {
            setError("Error al enviar el enlace de acceso");
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(74,222,128,0.22),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(14,116,144,0.2),_transparent_50%)]" />
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-green-400/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />

            <div className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-10">
                <div className="grid w-full gap-8 lg:grid-cols-[1.1fr_0.9fr]">
                    <div className="hidden lg:flex flex-col justify-between rounded-3xl border border-white/10 bg-slate-950/70 p-10 shadow-[0_0_40px_rgba(15,23,42,0.6)]">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.45em] text-green-200/70">
                                Plataforma SIPLY
                            </p>
                            <h1 className="mt-4 font-[var(--font-platform-display)] text-4xl">
                                Bienvenido a SIPLY
                            </h1>
                            <p className="mt-4 text-sm text-slate-300">
                                Ingresa para gestionar tus equipos, inventarios y solicitudes
                                de soporte dentro de tu empresa.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
                            <div className="mb-4 flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-green-400/15 flex items-center justify-center">
                                    <span className="text-green-200">⚡</span>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">Acceso directo</p>
                                    <p className="text-xs text-slate-400">
                                        Tus módulos activos en un solo panel.
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs text-slate-400">
                                Usa el correo con el que recibiste acceso a SIPLY.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col justify-center">
                        <div className="w-full rounded-3xl border border-white/10 bg-slate-950/80 p-8 shadow-[0_0_30px_rgba(15,23,42,0.5)] backdrop-blur-xl sm:p-10">
                            <div className="flex items-center justify-center mb-8">
                                <Image
                                    src="/images/siply_logo.svg"
                                    alt="Siply Logo"
                                    width={150}
                                    height={60}
                                    className="h-10 w-auto"
                                />
                            </div>

                            <h2 className="text-2xl font-semibold text-center">
                                Acceso a tu espacio de trabajo
                            </h2>
                            <p className="text-sm text-slate-300 text-center mt-2">
                                Administra personas, inventarios y soporte desde aquí.
                            </p>

                            {error && (
                                <Alert severity="error" className="mt-6">
                                    {error}
                                </Alert>
                            )}

                            <div className="mt-8 space-y-5">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={handleGoogleSignIn}
                                    disabled={isLoading}
                                    className="w-full border-white/15 text-white hover:bg-white/5"
                                >
                                    <Image
                                        src="/icons/g.webp"
                                        alt="Google"
                                        width={20}
                                        height={20}
                                        className="w-5 h-5 mr-2"
                                    />
                                    Continuar con Google
                                </Button>

                                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-slate-500">
                                    <span className="h-px flex-1 bg-white/10" />
                                    o
                                    <span className="h-px flex-1 bg-white/10" />
                                </div>

                                <form onSubmit={handleEmailSignIn} className="space-y-4">
                                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.3em] text-slate-400">
                                        Correo para acceso
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="usuario@sispro.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isLoading}
                                        className="w-full rounded-xl border-white/10 bg-slate-900/70 text-white placeholder:text-slate-500 focus-visible:ring-green-400/40 mt-1"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={isLoading || !email}
                                        className="w-full bg-green-400 text-slate-950 hover:bg-green-300"
                                    >
                                        {isLoading ? "Enviando..." : "Ingresar a SIPLY"}
                                    </Button>
                                </form>
                            </div>

                            <p className="mt-8 text-xs text-center text-slate-500">
                                Al continuar aceptas nuestras politicas de acceso y seguridad.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignInPage;
